/**
 * AP-31 Nachtrag — **ein Eingabefeld mit Vorschlägen im Dropdown.**
 *
 * Für PLZ und Ort der Adressmaske: Getippt wird frei; passende Vorschläge
 * erscheinen darunter und werden **nur übernommen, wenn man einen wählt** — per
 * Klick, oder mit Pfeiltasten und Enter. Wer keinen wählt, behält seine Eingabe.
 * Nichts wird automatisch eingetragen (Vorgabe des Auftraggebers vom 13.9.2026:
 * „9560" hatte sonst still einen Ort gesetzt, der zur getippten Adresse nicht
 * passte).
 *
 * Bewusst kein `<datalist>`: Deren Liste zeigt der Browser erst beim Klick ins
 * Feld und filtert sie selbst — ein Vorschlag „9400 Wolfsberg" zum getippten Ort
 * „wolfsberg" fiele dort heraus.
 */

import { useEffect, useId, useState } from 'react';

interface Props<T> {
  value: string;
  onChange: (wert: string) => void;
  vorschlaege: readonly T[];
  /** Was im Dropdown steht, z. B. „9400 Wolfsberg". */
  beschriftung: (v: T) => string;
  schluessel: (v: T) => string;
  onWaehlen: (v: T) => void;
  className?: string;
  required?: boolean;
  inputMode?: 'text' | 'numeric';
  /** AP-36: Das Feld hängt am Speicher-Baustein — Verlassen und Esc gehören ihm. */
  name?: string;
  disabled?: boolean;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function OrtVorschlagFeld<T>({
  value,
  onChange,
  vorschlaege,
  beschriftung,
  schluessel,
  onWaehlen,
  className,
  required,
  inputMode,
  name,
  disabled,
  onBlur,
  onKeyDown,
}: Props<T>) {
  const listenId = useId();
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState(-1);

  // Neue Vorschläge: Markierung zurücksetzen — sonst zeigte Enter auf einen
  // Eintrag, der gerade gar nicht mehr an dieser Stelle steht.
  useEffect(() => setMarkiert(-1), [vorschlaege]);

  const sichtbar = offen && vorschlaege.length > 0;

  const waehlen = (v: T) => {
    onWaehlen(v);
    setOffen(false);
    setMarkiert(-1);
  };

  const tasten = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!sichtbar) {
      if (e.key === 'ArrowDown' && vorschlaege.length) {
        setOffen(true);
        e.preventDefault();
        return;
      }
      // Esc und Enter gehören dann dem Feld (AP-36: Esc stellt den alten Wert her).
      onKeyDown?.(e);
      return;
    }
    if (e.key === 'ArrowDown') {
      setMarkiert((m) => Math.min(m + 1, vorschlaege.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setMarkiert((m) => Math.max(m - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && markiert >= 0) {
      // Enter übernimmt den markierten Vorschlag — und schickt das Formular nicht ab.
      waehlen(vorschlaege[markiert]);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setOffen(false);
      e.preventDefault();
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={sichtbar}
        aria-controls={listenId}
        aria-autocomplete="list"
        autoComplete="off"
        required={required}
        inputMode={inputMode}
        name={name}
        disabled={disabled}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOffen(true);
        }}
        onFocus={() => setOffen(true)}
        onBlur={() => {
          setOffen(false);
          onBlur?.();
        }}
        onKeyDown={tasten}
        className={className}
      />
      {sichtbar && (
        <ul
          id={listenId}
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 max-h-60 min-w-full overflow-auto rounded-md border border-border py-1 text-sm shadow-lg"
          // Overlays: Hintergrund direkt am Element (styling.md → Overlays).
          style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        >
          {vorschlaege.map((v, i) => (
            <li
              key={schluessel(v)}
              role="option"
              aria-selected={i === markiert}
              // mousedown statt click: Sonst schließt das Blur des Feldes die Liste,
              // bevor der Klick ankommt.
              onMouseDown={(e) => {
                e.preventDefault();
                waehlen(v);
              }}
              onMouseEnter={() => setMarkiert(i)}
              className={`cursor-pointer whitespace-nowrap px-3 py-1.5 ${i === markiert ? 'bg-muted' : ''}`}
            >
              {beschriftung(v)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
